import { trackGameFunnel } from "@/lib/analytics";
import { localDay, shiftDay } from "@/lib/tracking/local-day";

// Embudo de juegos para las métricas internas (/admin/estadisticas). Mismas
// listas que el server (game-funnel-daily.schema.ts).
export type FunnelGame = "daily-word" | "word-search" | "hub";
export type PromoPlacement = "home" | "events" | "viva_cine" | "viva_cine_title" | "menu";

// Día (local) en que este navegador jugó por última vez cada juego. Solo
// fechas, ningún identificador.
const PLAYED_KEY = "cv_games_played";

function readPlayed(): Partial<Record<FunnelGame, string>> {
  try {
    return JSON.parse(localStorage.getItem(PLAYED_KEY) ?? "{}") as Partial<Record<FunnelGame, string>>;
  } catch {
    return {};
  }
}

// Llamar al empezar una partida. Cuenta una vez por juego y día, con la
// racha: primera vez, también jugó ayer, o jugó antes pero no ayer.
export function trackGamePlayer(game: Exclude<FunnelGame, "hub">): void {
  const today = localDay();
  const played = readPlayed();
  const last = played[game];
  if (last === today) return;
  const step = !last ? "player_first" : last === shiftDay(today, -1) ? "player_yesterday" : "player_earlier";
  try {
    localStorage.setItem(PLAYED_KEY, JSON.stringify({ ...played, [game]: today }));
  } catch {
    // Almacenamiento bloqueado: cada partida cuenta como primera vez.
  }
  trackGameFunnel(game, step);
}

export function trackGameShare(game: Exclude<FunnelGame, "hub">): void {
  trackGameFunnel(game, "share");
}

export function trackGamePromoClick(game: FunnelGame, placement: PromoPlacement): void {
  trackGameFunnel(game, "promo_click", placement);
}

// Una vista por pestaña y lugar (recargar o volver a la página no suma).
const SEEN_KEY = "cv_game_promos_seen";

export function trackGamePromoView(game: FunnelGame, placement: PromoPlacement): void {
  const key = `${game}|${placement}`;
  try {
    const seen = JSON.parse(sessionStorage.getItem(SEEN_KEY) ?? "[]") as string[];
    if (seen.includes(key)) return;
    sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seen, key]));
  } catch {
    // Almacenamiento bloqueado: se cuenta por carga de página.
  }
  trackGameFunnel(game, "promo_view", placement);
}

// Juego al que lleva una ruta (para las visitas desde un link compartido).
export function gameForPath(path: string): Exclude<FunnelGame, "hub"> | null {
  if (/\/juegos\/palabra-del-dia(\/|$)/.test(path)) return "daily-word";
  if (/\/juegos\/sopa-de-letras(\/|$)/.test(path)) return "word-search";
  return null;
}
